import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { NotificationBell } from './notifications/NotificationBell';
import { getInitials } from '../lib/initials';
import { useQuery } from '@tanstack/react-query';
import { getUnreadCount } from '../services/api/notifications';

export function Navigation() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      {/* Top bar */}
      <nav className="bg-primary-green shadow-lg sticky top-0 z-50">
        <div className="mx-auto max-w-7xl w-full px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link to="/" className="flex items-center space-x-2 no-underline hover:no-underline">
                <img
                  src="/icon-192x192.png"
                  alt="Gribklan Logo"
                  className="w-10 h-10 sm:w-14 sm:h-14 rounded-none flex-shrink-0"
                  style={{ background: 'none' }}
                />
                <span className="text-white font-heading font-bold text-2xl sm:text-2xl leading-none tracking-tight whitespace-nowrap">
                  Gribklan <span className="font-light text-white/80">social</span>
                </span>
              </Link>
              <span className="text-white/40 text-xs font-mono hidden sm:inline" title={`Build: ${__GIT_HASH__}`}>
                v{__APP_VERSION__}
              </span>
            </div>

            {/* Desktop navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {isAuthenticated ? (
                <>
                  <NavLink to="/members" active={isActive('/members')}>
                    Medlemmer
                  </NavLink>
                  <div className="flex items-center space-x-4 ml-4">
                    <NotificationBell />
                    <Link
                      to={`/profile/${user?.id}`}
                      className="flex items-center gap-2 text-white hover:text-white/80 transition-colors no-underline hover:no-underline"
                    >
                      {user?.profileImageUrl ? (
                        <img
                          src={user.profileImageUrl}
                          alt={user.name}
                          className="w-8 h-8 rounded-full object-cover border-2 border-white/40"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center text-xs font-bold text-white">
                          {getInitials(user?.name || '')}
                        </div>
                      )}
                      <span className="text-sm">{user?.name}</span>
                    </Link>
                  </div>
                </>
              ) : (
                <NavLink to="/login" active={isActive('/login')}>
                  Log ind
                </NavLink>
              )}
            </div>

            {/* Mobile: profile avatar top-right */}
            {isAuthenticated && (
              <Link
                to={`/profile/${user?.id}`}
                className="lg:hidden flex-shrink-0 no-underline hover:no-underline"
              >
                {user?.profileImageUrl ? (
                  <img
                    src={user.profileImageUrl}
                    alt={user?.name}
                    className="w-9 h-9 rounded-full object-cover border-2 border-white/40"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center text-sm font-bold text-white">
                    {getInitials(user?.name || '')}
                  </div>
                )}
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile bottom navigation */}
      {isAuthenticated && (
        <BottomNav isActive={isActive} />
      )}
    </>
  );
}

interface BottomNavProps {
  isActive: (path: string) => boolean;
}

function BottomNav({ isActive }: BottomNavProps) {
  const { data: unreadNotifications = 0 } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: 30000,
  });

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-primary-green border-t border-secondary-green/30 pb-safe">
      <div className="flex items-stretch justify-around h-16">
        <BottomNavItem to="/" active={isActive('/')} label="Hjem">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </BottomNavItem>

        <BottomNavItem to="/notifications" active={isActive('/notifications')} label="Notifikationer" badge={unreadNotifications}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </BottomNavItem>

        <BottomNavItem to="/members" active={isActive('/members')} label="Medlemmer">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </BottomNavItem>
      </div>
    </nav>
  );
}

interface BottomNavItemProps {
  to: string;
  active: boolean;
  label: string;
  badge?: number;
  children: React.ReactNode;
}

function BottomNavItem({ to, active, label, badge, children }: BottomNavItemProps) {
  return (
    <Link
      to={to}
      className={`flex flex-col items-center justify-center flex-1 gap-0.5 no-underline hover:no-underline transition-colors ${
        active ? 'text-white' : 'text-white/50 hover:text-white/80'
      }`}
    >
      <div className="relative">
        {children}
        {badge != null && badge > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center leading-none">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      <span className="text-xs leading-none">{label}</span>
    </Link>
  );
}

interface NavLinkProps {
  to: string;
  active: boolean;
  children: React.ReactNode;
}

function NavLink({ to, active, children }: NavLinkProps) {
  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-lg transition-kfum text-sm lg:text-base no-underline hover:no-underline ${
        active
          ? 'bg-secondary-green text-white'
          : 'text-white hover:bg-secondary-green'
      }`}
    >
      {children}
    </Link>
  );
}


